import { DafPrivilegeTypeItem, useAddPrivilegeToCartMutation } from '@generated'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useCart } from '@lib/hooks/useCart'
import { getKontentImage } from '@lib/kontent'
import { PrivilegeItem } from '@lib/kontent/models/PrivilegeItem'
import { useCartReferenceStore } from '@components/LoadCart'
import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'

type Status = 'idle' | 'redeemed' | 'in-cart' | 'loading'

type Props = {
  dafItem: DafPrivilegeTypeItem
  elements: PrivilegeItem
  handleRequestClose: () => void
}

export function usePrivilegeItem({ dafItem, elements, handleRequestClose }: Props) {
  const [buttonStatus, setButtonStatus] = useState<Status>('idle')
  const cart = useCart()
  const { addNotification } = useNotifications()
  const [, addPrivilegeItemToCart] = useAddPrivilegeToCartMutation()
  const cartReferenceStore = useCartReferenceStore()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const reference = cartReferenceStore.reference
  const isAvailable = dafItem?.beschikbaar ? dafItem.beschikbaar > 0 : false
  const router = useRouter()
  const { parseGraphQlError } = useLocalization()

  useEffect(() => {
    if (dafItem.privilege_action === 'SALESORDER') {
      // If the item is a sales order, we need to check if the cart already contains the item
      // and, if so, check if the quantity in the cart does not exceed the maximum allowed quantity
      if (cart && cart.privilegeItems) {
        const currentItemsAlreadyInCart = cart.privilegeItems.filter(
          (item) => item?.privilegeId === dafItem?.privilege_nr?.toString(),
        )
        if (dafItem?.beschikbaar && currentItemsAlreadyInCart.length < dafItem?.beschikbaar) {
          setButtonStatus('idle')
        } else {
          setButtonStatus('in-cart')
        }
      }
    } else {
      setButtonStatus(isAvailable ? 'idle' : 'redeemed')
    }
  }, [cart]) // eslint-disable-line

  async function handleAddToCart() {
    setButtonStatus('loading')
    const { data, error } = await addPrivilegeItemToCart({
      input: {
        privilegeId: dafItem?.privilege_nr?.toString() || '',
        imageSource: getKontentImage(elements.image___placeholder_image.value[0].url, {
          width: 1024,
        }),
        reference: reference as string,
      },
    })

    if (error) {
      setButtonStatus('idle')
      addNotification({
        variant: 'error',
        label: parseGraphQlError(error.message),
      })
    }

    if (data && data.addPrivilegeToCart) {
      const prefix = `/${language}`
      addNotification({
        variant: 'image-action',
        label: elements.title.value,
        sublabel: 'Privilege',
        image: getKontentImage(elements.image___placeholder_image.value[0].url, {
          width: 180,
        }),
        description: t('addedToShoppingBag', { ns: 'other' }),
        actionLabel: t('toCheckout', { ns: 'other' }),
        onClickAction: () => router.push(`${prefix}${t('checkout.index', { ns: 'navigation' })}`),
      })
      handleRequestClose()
    }
  }

  return {
    buttonStatus,
    isAvailable,
    handleAddToCart,
  }
}
